import 'cropperjs/dist/cropper.css'

import first from 'lodash/first'
import React, { createRef, RefObject, useCallback, useLayoutEffect, useRef, useState, VFC } from 'react'
import styled, { css } from 'styled-components'

import { Button } from '@Components/Button'
import { BuyButton } from '@Components/BuyButton'
import { cropperSetup } from '@Components/Personalization/Modals/UVPrint/cropperSetup'
import { UVPrintState } from '@Components/Personalization/Modals/UVPrint/index'
import { fetchRetry } from '@Components/Personalization/Modals/UVPrint/upload-image'
import { UVPrintInfo } from '@Components/Personalization/Modals/UVPrint/UVPrintInfo'
import {
  PersonalizationArea,
  PersonalizationCaption,
  PersonalizationImageWatch,
  PersonalizationInformation,
  PersonalizationItemRoot,
  PersonalizationTitle,
  PersonalizationUploadWrap,
} from '@Components/Personalization/Styles/Personalization.styled'
import { Product, useCreatePersonalizationBlobMutation } from '@Generated/graphql'
import { up } from '@Globals/Style/breakpoints'
import { absoluteCenter, imageLoadingCircle } from '@Globals/Style/mixins'
import { useTranslation } from '@Globals/Translations'

interface Props {
  additionalIDs?: string[]
  cropperRef: RefObject<Cropper>
  inputRef: RefObject<HTMLInputElement>
  onClose: () => void
  price: string
  product: Product
  state: UVPrintState
  updateProduct?: boolean
}

const MaskCanvas = styled.canvas`
  z-index: 50;
  ${absoluteCenter}
`

const RangeSlider = styled.input`
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  outline: none;
  -webkit-transition: .2s;
  transition: opacity .2s;
  background: #e5e5e5;
  padding: 0;
  border-radius: 16px;
  height: 8px;

  &:hover {
    cursor: pointer;
  }

  ::-webkit-slider-thumb {
    -webkit-appearance: none; /* Override default look */
    appearance: none;
    padding: 11px;
    background: #336a90;
    width: 11px;
    border-radius: 50%;
  }

  ::-moz-range-thumb {
    background: #336a90;
    width: 22px;
    height: 22px;
    padding: 0;
    border: 0;
    border-radius: 50%;
  }
`

const RangeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  ${up('tablet')} {
    width: 50%;
  }
`

const RangeLabel = styled.label`
  font-size: 12px;
  text-transform: uppercase;
  font-weight: 600;
  padding-bottom: 16px;
`

const ControlArea = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 10px 16px 20px;
  max-width: 100%;
  width: 100%;
  margin: 0 auto;

  & > :first-child {
    margin-bottom: 40px;
  }

  ${up('tablet')} {

    & > * {
      margin: 0 20px;
    }

    & > :first-child {
      margin: 0 20px 0 0;
    }

    & > :last-child {
      margin: 0 0 0 20px;
    }
  }

  ${up('tablet')} {
    flex-direction: row;
    flex-wrap: nowrap;
    max-width: unset;
  }
`

const DrawBox = styled.div(({ theme }) => css`
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: ${theme.borderRadius};
  background: transparent;
  pointer-events: none;
  z-index: 200;
  position: absolute;
  overflow: hidden;
`)

const StyledItemRoot = styled(PersonalizationItemRoot)(({ theme }) => css`
  padding-top: 16px;
  margin-bottom: 0;

  &:last-of-type {
    margin-bottom: 0;
  }

  border-radius: ${theme.borderRadius};
  overflow: hidden;
`)

const StyledTitle = styled(PersonalizationTitle)`
  padding-bottom: 0;
  margin-bottom: 0;
`

const StyledCaption = styled(PersonalizationCaption)`
  margin-bottom: 31px;

  ${up('tablet')} {
    margin-bottom: 0;
  }
`

const UVPrintTopContainer = styled.div(({ theme }) => css`
  max-width: 100%;
  width: 100%;


  ${up('tablet')} {
    background: ${theme.colors.greyLightest};
  }
`)

const UVPrintInformation = styled(PersonalizationInformation)`
  ${up('tablet')} {
    max-width: unset;
    background: white;
    padding-bottom: 40px;
  }
`

const UVButtons = styled.div`
  ${up('tablet')} {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;

    & > * {
      margin: 0 20px;
    }

    & > :first-child {
      margin: 0 0 0 20px;

    }

    & > :last-child {
      margin: 0 20px 0 0;
    }

  }
`

const UVButtonsWrapper = styled.div<{ order: number }>(({ order }) => css`
  display: flex;
  flex-direction: column;

  ${up('tablet')} {
    width: 50%;
    order: ${order};
    padding-top: 20px;
    padding-bottom: 20px;
  }
`)

const UVSpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 375px;
  z-index: 1000;
  width: 230px;
  margin: 0 auto;

`

const UVSpinner = styled.div`
  ${imageLoadingCircle({ borderWidth: '3px', size: '70px' })};
  color: #555555;
`

const UVPrintTitle = styled(PersonalizationTitle)`
  ${up('tablet')} {
    display: none;
  }
`

const UVPrintStepTwo: VFC<Props> = ({
  additionalIDs,
  cropperRef,
  inputRef,
  onClose,
  price,
  product,
  state,
  updateProduct = false,
}) => {
  const { t } = useTranslation('products')
  const containerRef = createRef<HTMLDivElement>()
  const maskRef = createRef<HTMLCanvasElement>()
  const canvasRef = createRef<HTMLCanvasElement>()
  const zoomRef = createRef<HTMLInputElement>()
  const rotateRef = createRef<HTMLInputElement>()
  const drawBoxRef = useRef<HTMLDivElement>()
  const [loading, setLoading] = useState(true)
  const [createBlob] = useCreatePersonalizationBlobMutation()

  useLayoutEffect(() => {
    if (!state.imageState?.image) {
      setLoading(true)
    }
    const isRefsReady = (maskRef.current && zoomRef.current && canvasRef.current && rotateRef.current && drawBoxRef.current) !== null
    if (state.imageState?.image) {
      setLoading(false)
      if (isRefsReady) {
        cropperSetup(state.imageState.image, {
          maskRef,
          canvasRef,
          drawBoxRef,
          zoomRef,
          rotateRef,
          cropperRef,
        })
      }
    }
  }, [canvasRef, drawBoxRef, maskRef, state, rotateRef, zoomRef, cropperRef])

  const getPersonalizationData = useCallback(async () => {
    if (!cropperRef.current) {
      return undefined
    }
    const name = first(inputRef?.current?.files)?.name || ''
    const cropper = cropperRef.current
    return fetchRetry({
      createBlob,
      cropper,
      name,
      tries: 10,
      product,
    })
  }, [createBlob, cropperRef, inputRef, product])

  if (!state) {
    return null
  }

  return (
    <StyledItemRoot ref={containerRef}>

      <StyledTitle>{t('UV_PRINT_TITLE_STEP_2')}</StyledTitle>
      <UVPrintTopContainer>
        {loading && <UVSpinnerWrapper><UVSpinner /></UVSpinnerWrapper>}
        {!loading && (
          <PersonalizationArea>
            <PersonalizationImageWatch
              alt="watch template"
              src="https://res.cloudinary.com/trendhim/image/upload/f_auto,c_pad,q_auto,w_320/frontend/personalisation/uv-print/watches/watch-template.png"
            />
            <MaskCanvas ref={maskRef} />
            <PersonalizationUploadWrap>
              <canvas ref={canvasRef} />
            </PersonalizationUploadWrap>
            <DrawBox ref={drawBoxRef as RefObject<HTMLDivElement>} />
          </PersonalizationArea>
        )}
        <ControlArea>
          <RangeContainer>
            <RangeLabel>{t('UV_PRINT_ROTATE')}</RangeLabel>
            <RangeSlider defaultValue="0" max="100" min="0" ref={rotateRef} type="range" />
          </RangeContainer>
          <RangeContainer>
            <RangeLabel>{t('UV_PRINT_ZOOM')}</RangeLabel>
            <RangeSlider
              defaultValue="0"
              max="1"
              min="0"
              ref={zoomRef}
              step="0.0001"
              type="range"
            />
          </RangeContainer>
        </ControlArea>
      </UVPrintTopContainer>
      <UVPrintInformation>
        <UVPrintTitle>
          {t('UV_PRINT_PERSONALIZED_TITLE')}
        </UVPrintTitle>

        <UVButtons>
          <UVButtonsWrapper order={2}>
            <BuyButton
              additionalIDs={additionalIDs}
              getPersonalizationData={getPersonalizationData}
              isSaleable
              postProcess={onClose}
              product={product}
              showPrice={false}
              unwrapped
              updateProduct={updateProduct}
            />
            <StyledCaption center>{t('UV_PRINT_ACTION_CAPTION')}</StyledCaption>
          </UVButtonsWrapper>
          <UVButtonsWrapper order={1}>
            <Button
              color="dark"
              onClick={() => {
                inputRef?.current?.click()
              }}
              variant="outlined"
            >
              {t('UV_PRINT_UPLOAD_NEW_CAPTION')}
            </Button>
            <StyledCaption center>
              {t('UV_PRINT_UPLOAD_NEW_LABEL')}
            </StyledCaption>
          </UVButtonsWrapper>
        </UVButtons>
        <UVPrintInfo price={price || ''} step={2} />
      </UVPrintInformation>
    </StyledItemRoot>
  )
}

export default UVPrintStepTwo

